import {
  Component,
  OnInit,
  ViewChild
} from '@angular/core';

// 1) Import Chart.js + ng2-charts types
import { ChartOptions, ChartData, ChartEvent } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { DfsService } from '../../../services/dfs.service';
import { MonthlyCashflow } from '../../../models/dfs-events.models';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-wallet-summary',
  imports: [
    CommonModule,
    BaseChartDirective
  ],
  templateUrl: './wallet-summary.component.html',
  styleUrls: ['./wallet-summary.component.scss']
})
export class WalletSummaryComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  isLoading = true;
  hasError = false;

  // Flags and text for positive/negative growth
  positiveEarn = false;
  negativeEarn = false;
  earnUptoText = '';

  // Chart type
  public lineChartType = 'line' as const;

  // Chart options
  public lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // hide top legend
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const yVal = context.parsed.y ?? 0;
            return 'Balance: £' + this.numberWithCommas(yVal);
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: '#D8DAE5',
          tickBorderDash: [10],
        },
        ticks: {
          display: true, // Hide X-axis ticks
        },
      },
      y: {
        display: true // Hide Y-axis
      }
    }
  };

  // Data container for ng2-charts
  public lineChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        label: 'Wallet Balance',
        data: [],
        fill: true,
        tension: 0.3,
        // We'll set gradient via plugin or color array as an example
        backgroundColor: 'rgba(75, 162, 174, 0.2)', // fallback color
        borderColor: '#4ba2ae',
        borderWidth: 2
      },
    ]
  };

  constructor(private walletService: DfsService) {}

  ngOnInit(): void {
    // Call our service
    this.walletService.getMonthlyCashflow().subscribe({
      next: (res) => {
        this.isLoading = false;
        this.setupChartData(res.data);
      },
      error: (err) => {
        this.isLoading = false;
        this.hasError = true;
        console.error('Error fetching monthly-cashflow:', err);
      }
    });
  }

  /**
   * Process the data from API, compute differences, and update chart data.
   */
  private setupChartData(data: MonthlyCashflow[]) {
    // Reverse data (just like the original code)
    const reversedData = [...data].reverse();

    // Extract labels (months) and dataset (cashFlow)
    const labels = reversedData.map(x => x.month);
    const dataSet = reversedData.map(x => x.cashFlow);

    // Compute percentage difference if at least 2 points
    if (dataSet.length >= 2) {
      const lastValue = dataSet[dataSet.length - 1];
      const prevValue = dataSet[dataSet.length - 2];
      const diff = ((lastValue - prevValue) / prevValue) * 100;

      if (diff > 0) {
        this.positiveEarn = true;
        this.negativeEarn = false;
        this.earnUptoText = diff.toFixed(2) + '%';
      } else if (diff < 0) {
        this.positiveEarn = false;
        this.negativeEarn = true;
        this.earnUptoText = diff.toFixed(2) + '%';
      } else {
        this.positiveEarn = false;
        this.negativeEarn = false;
        this.earnUptoText = ''; // exactly 0
      }
    } else {
      // Hide both if only 1 or 0 data points
      this.positiveEarn = false;
      this.negativeEarn = false;
      this.earnUptoText = '';
    }

    // Update the chart data
    this.lineChartData.labels = labels;
    this.lineChartData.datasets[0].data = dataSet;

    // Optionally generate a gradient background for the first dataset
   // this.setGradientBackground();

    // If the chart is already rendered, refresh it
    this.chart?.update();
  }

  /**
   * Optionally create a gradient background, if desired
   */
  // private setGradientBackground(): void {
  //   if (!this.chart) {
  //     return;
  //   }
  //   // Use the chart's rendering context to create a gradient
  //   const ctx = (this.chart.ctx) as CanvasRenderingContext2D;
  //   if (!ctx) {
  //     return;
  //   }

  //   const gradient = ctx.createLinearGradient(0, 0, 0, 400);
  //   gradient.addColorStop(0, '#4ba2ae33');
  //   gradient.addColorStop(1, '#4ba2ae00');

  //   this.lineChartData.datasets[0].backgroundColor = gradient;
  // }

  /**
   * Format number with commas
   */
  private numberWithCommas(value: number): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}
